import { electronService } from '@services/electron/electron.service';
import { logger } from '@services/logger/logger.service';
import { getErrorCode } from '@services/logger/scrubFilePaths';

export async function removeFile(filePath: string): Promise<boolean> {
  try {
    await window.electron.removeFile(filePath);
    logger.info('DESKTOP', 'Successfully removed file.');
    return true;
  } catch (err) {
    logger.error('DESKTOP', `Failed to remove file (${getErrorCode(err)}).`, undefined, false);
    return false;
  }
}

export async function removeFiles(filePaths: string[]) {
  logger.info('DESKTOP', `Starting removal of ${filePaths.length} file(s)`);
  const results = await Promise.all(filePaths.map(removeFile));
  const successCount = results.filter(Boolean).length;

  logger.info(
    'DESKTOP',
    `Removal completed: ${successCount} succeeded, ${results.length - successCount} failed`,
  );
}

export async function moveFileOrDelete(from: string, to: string | string[]) {
  const fileName = await electronService.getBaseName(from);
  const destinationFolder = to instanceof Array ? to : [to];
  const destinationPath = await electronService.joinPaths(...destinationFolder, fileName);

  try {
    logger.info('DESKTOP', 'Moving file to its destination folder.');
    await window.electron.moveFile(from, destinationPath, { overwrite: true });
  } catch (movingErr) {
    const moveCode = getErrorCode(movingErr);
    // If moving failed, try to delete the file.
    try {
      await window.electron.removeFile(from);
      logger.error(
        'DESKTOP',
        `Failed to move file (${moveCode}). Deleted it instead.`,
        undefined,
        false,
      );
    } catch (deleteErr) {
      logger.error(
        'DESKTOP',
        `Failed to move file (${moveCode}), and failed to remove it (${getErrorCode(deleteErr)}).`,
        undefined,
        false,
      );
    }
  }
}

export async function getFilesPathInDirectories(directories: string[]) {
  const results: string[] = [];
  for (const directory of directories) {
    logger.info('DESKTOP', 'Scanning directory.');
    const filePaths = await electronService.getAllFilePathsFromDir(directory);
    logger.info('DESKTOP', `Found ${filePaths.length} file(s).`);

    const validPaths = await Promise.all(
      filePaths.map(async (path) => ({
        path,
        isValid: await isValidFilePath(path),
      })),
    );
    const validCount = validPaths.filter(({ isValid }) => isValid).length;
    logger.info('DESKTOP', `${validCount} valid file(s) (excluded .DS_Store files)`);

    results.push(...validPaths.filter(({ isValid }) => isValid).map(({ path }) => path));
  }
  return results;
}

async function isValidFilePath(path: string) {
  const fileName = await electronService.getBaseName(path);

  return !(fileName === '.DS_Store');
}
